import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { config } from '../../config';
import Spinner from '../Global/Spinner';

const API_URL = config.API_URI;

const AVAILABLE_SCOPES = [
  { id: 'boards:read', label: 'Read boards' },
  { id: 'boards:write', label: 'Write boards' },
  { id: 'cards:read', label: 'Read cards' },
  { id: 'cards:write', label: 'Write cards' },
  { id: 'lists:read', label: 'Read lists' },
  { id: 'lists:write', label: 'Write lists' },
  { id: 'pages:read', label: 'Read pages' },
  { id: 'pages:write', label: 'Write pages' },
  { id: 'workspaces:read', label: 'Read workspaces' },
  { id: 'workspaces:write', label: 'Write workspaces' },
  { id: 'members:read', label: 'Read members' },
  { id: 'members:write', label: 'Write members' },
  { id: 'ai:read', label: 'AI read' },
  { id: 'ai:write', label: 'AI write' },
  { id: 'analytics:read', label: 'Analytics' },
  { id: 'webhooks:read', label: 'Read webhooks' },
  { id: 'webhooks:write', label: 'Write webhooks' },
];

const ApiKeysSection = ({ workspace }) => {
  const dispatch = useDispatch();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([
    'boards:read', 'boards:write', 'cards:read', 'cards:write',
    'lists:read', 'lists:write', 'pages:read', 'workspaces:read',
    'ai:read', 'ai:write',
  ]);
  const [generatedKey, setGeneratedKey] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchKeys = useCallback(async () => {
    if (!workspace?.shortId) return;
    try {
      const response = await fetch(
        `${API_URL}/v1/api-keys?workspaceId=${workspace.shortId}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setKeys(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setLoading(false);
    }
  }, [workspace?.shortId]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      dispatch(showSnackBar({ message: 'Please enter a key name', type: 'error' }));
      return;
    }
    if (selectedScopes.length === 0) {
      dispatch(showSnackBar({ message: 'Select at least one permission', type: 'error' }));
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/v1/api-keys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newKeyName.trim(),
          workspaceId: workspace.shortId,
          scopes: selectedScopes,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create key');

      setGeneratedKey(data.data.key);
      dispatch(showSnackBar({ message: 'API key created', type: 'success' }));
      fetchKeys();
    } catch (err) {
      dispatch(showSnackBar({ message: err.message, type: 'error' }));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      const response = await fetch(`${API_URL}/v1/api-keys/${keyId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        dispatch(showSnackBar({ message: 'API key revoked', type: 'success' }));
        fetchKeys();
      }
    } catch (err) {
      dispatch(showSnackBar({ message: 'Failed to revoke key', type: 'error' }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (scopeId) => {
    setSelectedScopes(prev =>
      prev.includes(scopeId)
        ? prev.filter(s => s !== scopeId)
        : [...prev, scopeId]
    );
  };

  const resetForm = () => {
    setShowCreate(false);
    setGeneratedKey('');
    setNewKeyName('');
    setSelectedScopes([
      'boards:read', 'boards:write', 'cards:read', 'cards:write',
      'lists:read', 'lists:write', 'pages:read', 'workspaces:read',
      'ai:read', 'ai:write',
    ]);
  };

  if (loading) return <div className="setting-row"><Spinner /></div>;

  return (
    <div className="api-keys-section">
      <div className="setting-row">
        <div className="setting-label">
          <h2>API Keys</h2>
          <p>Generate keys to use with the Zoobbe CLI, AI agents, or third-party integrations.</p>
        </div>
        <div className="api-keys-content">
          {/* Generated key display */}
          {generatedKey && (
            <div className="api-key-generated">
              <div className="api-key-warning">
                Save this key now — you won't see it again.
              </div>
              <div className="api-key-display">
                <code className="api-key-code">{generatedKey}</code>
                <button
                  className="api-key-copy-btn"
                  onClick={() => copyToClipboard(generatedKey)}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="api-key-usage-hints">
                <div className="usage-hint">
                  <strong>CLI:</strong>
                  <code>zoobbe auth login --token {generatedKey.substring(0, 20)}...</code>
                </div>
                <div className="usage-hint">
                  <strong>API:</strong>
                  <code>Authorization: Bearer {generatedKey.substring(0, 20)}...</code>
                </div>
              </div>
              <button className="api-key-done-btn" onClick={resetForm}>Done</button>
            </div>
          )}

          {/* Create form */}
          {showCreate && !generatedKey && (
            <div className="api-key-create-form">
              <div className="form-field">
                <label>Key Name</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., CLI Access, CI/CD Pipeline"
                  className="api-key-input"
                  maxLength={100}
                />
              </div>
              <div className="form-field">
                <label>
                  Permissions
                  <button
                    className="select-all-link"
                    onClick={() => setSelectedScopes(AVAILABLE_SCOPES.map(s => s.id))}
                  >
                    Select all
                  </button>
                </label>
                <div className="scopes-grid">
                  {AVAILABLE_SCOPES.map(scope => (
                    <label key={scope.id} className="scope-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(scope.id)}
                        onChange={() => toggleScope(scope.id)}
                      />
                      <span>{scope.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="api-key-create-btn"
                  onClick={handleCreate}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Key'}
                </button>
                <button className="api-key-cancel-btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Existing keys list */}
          {!showCreate && !generatedKey && (
            <>
              <button
                className="api-key-add-btn"
                onClick={() => setShowCreate(true)}
              >
                + Create API Key
              </button>

              {keys.length > 0 ? (
                <div className="api-keys-list">
                  {keys.map(key => (
                    <div key={key._id} className={`api-key-item ${!key.isActive ? 'revoked' : ''}`}>
                      <div className="api-key-info">
                        <div className="api-key-name">{key.name}</div>
                        <div className="api-key-meta">
                          <code className="api-key-prefix">{key.prefix}...</code>
                          <span className="api-key-separator">·</span>
                          <span>{key.scopes?.length || 0} permissions</span>
                          <span className="api-key-separator">·</span>
                          <span>
                            {key.lastUsedAt
                              ? `Used ${new Date(key.lastUsedAt).toLocaleDateString()}`
                              : 'Never used'}
                          </span>
                          {!key.isActive && (
                            <span className="api-key-badge revoked">Revoked</span>
                          )}
                        </div>
                      </div>
                      {key.isActive && (
                        <button
                          className="api-key-revoke-btn"
                          onClick={() => handleRevoke(key._id)}
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="api-keys-empty">
                  No API keys yet. Create one to use the CLI or connect AI tools.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeysSection;
